import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import AppContext from "../contexts/App";
import { components } from "../schema";

import SecondaryNav, { SecondaryNavTitle } from "./SecondaryNav";
import { getAndReturnJSON, postAndReturnJSON } from "../helpers/api";
import Loader from "./Loader";
import AutocompleteFormGroup from "./Form/AutocompleteFormGroup";

type UserProfileType = components["schemas"]["UserProfile"];
type Customer = components["schemas"]["Customer"];
type UserProfilePayee = components["schemas"]["UserProfilePayee"];

interface OptionType {
  value: any;
  label: string;
}

interface UserProfileProps {
  id: number;
  customer: Customer | null;
  payee: UserProfilePayee | null;
  setActiveProfile: (id: number) => void;
  active: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({
  id,
  customer,
  payee,
  setActiveProfile,
  active,
}) => {
  return (
    <button
      type="button"
      className={`list-group-item list-group-item-action`}
      onClick={() => {
        setActiveProfile(id);
      }}
    >
      {customer && customer.name}
      {customer && <span className="ml-2 badge badge-primary">Admin</span>}
      {payee && payee.name}
      {payee && <span className="ml-2 badge badge-warning">Payee</span>}
      {payee && payee.customer_data && (
        <span className="ml-2 badge badge-info">
          {payee.customer_data.name}
        </span>
      )}
      {active && <span className="ml-2 badge badge-success">Active</span>}
    </button>
  );
};

interface UserProfileListProps {
  onSwitch?: (() => void) | null;
}

interface UserProfilesResponse {
  results: UserProfileType[];
}

export const UserProfileList: React.FC<UserProfileListProps> = ({
  onSwitch = null,
}) => {
  const { role, userIsStaff, userId, userProfile, setUserProfile } =
    useContext(AppContext);
  const profileId = userProfile?.id;
  const [userProfiles, setUserProfiles] = useState<UserProfileType[] | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<OptionType | null>(null);
  const [payee, setPayee] = useState<OptionType | null>(null);
  const activeProfile = profileId && parseInt(profileId.toString());
  const isStaffOrCustomer = userIsStaff || role === "customer";
  const history = useHistory();

  // load data on mount
  useEffect(() => {
    setLoading(true);
    getAndReturnJSON({
      url: "/api/v1/user-profiles/",
      params: { user: userId },
    }).then((response: UserProfilesResponse) => {
      setUserProfiles(response.results);
      setLoading(false);
    });
  }, [userId]);

  const handleProfileChange = (id: number | false) => {
    postAndReturnJSON({
      url: "/api/v1/set-active-user-profile/",
      data: { active_user_profile: id },
    }).then((response: UserProfileType) => {
      setUserProfile(response);
      if (onSwitch) onSwitch();
      else history.push("/");
    });
  };

  if (loading) return <Loader />;

  const _userProfiles =
    userProfiles &&
    userProfiles.map((userProfile) => (
      <UserProfile
        key={userProfile.id}
        active={userProfile.id === activeProfile}
        setActiveProfile={handleProfileChange}
        {...userProfile}
      />
    ));

  return (
    <div className="list-group">
      {userIsStaff && (
        <button
          type="button"
          className={`list-group-item list-group-item-action`}
          onClick={() => handleProfileChange(false)}
        >
          Staff
        </button>
      )}
      {_userProfiles}
      {isStaffOrCustomer && (
        <div className="mt-4">
          <h5 className="mb-4">
            Choose a specific {userIsStaff && "customer or"} payee
          </h5>
          {userIsStaff && (
            <>
              <AutocompleteFormGroup
                customerId=""
                role={role}
                label="Customer"
                id="customer"
                autocompleteUrl="/api/v1/autocomplete/customer/"
                onChange={(value) => {
                  setPayee(null);
                  setCustomer(value as OptionType | null);
                  if (value && !Array.isArray(value)) {
                    postAndReturnJSON({
                      url: "/api/v1/set-active-user-profile/",
                      data: { active_user_profile_customer_id: value.value },
                    }).then((response: UserProfileType) => {
                      setUserProfile(response);
                      history.push("/");
                    });
                  }
                }}
                value={customer}
                showAll={true}
              />
            </>
          )}
          <AutocompleteFormGroup
            customerId=""
            role={role}
            label="Payee"
            id="payee"
            autocompleteUrl="/api/v1/autocomplete/payee/"
            onChange={(value) => {
              setCustomer(null);
              setPayee(value as OptionType | null);
              if (value && !Array.isArray(value)) {
                const data: any = {
                  active_user_profile_payee_id: value.value,
                };
                if (role === "customer") {
                  data["active_user_profile"] =
                    profileId && parseInt(profileId.toString());
                }
                postAndReturnJSON({
                  url: "/api/v1/set-active-user-profile/",
                  data: data,
                }).then((response: UserProfileType) => {
                  setUserProfile(response);
                  history.push("/");
                });
              }
            }}
            value={payee}
            showAll={true}
          />
        </div>
      )}
    </div>
  );
};

const UserProfiles: React.FC = () => {
  useEffect(() => {
    document.title = `Switch Account · Infinite Catalog`;
  }, []);

  return (
    <div>
      <SecondaryNav>
        <SecondaryNavTitle title="Switch Account" />
      </SecondaryNav>
      <div className="container-fluid">
        <UserProfileList />
      </div>
    </div>
  );
};

export default UserProfiles;
